import { createActions } from 'redux-actions';
import { put, select, call, race, take } from 'redux-saga/effects';
import { LOCATION_CHANGE } from 'react-router-redux';
import {
  apiActionResult,
  apiActions as commonApiActions,
  apiResourceActionCreator
} from '../../common';
import { authTokenSelector } from '../../session';
import { API_ENDPOINT } from './endpoints';
import getBusinessTasksConfig from './getBusinessTasks';
import { createPollingWorker } from './pollingUtils';

const actions = createActions({
  TASK: {
    GET_BUSINESS_SYNC_STATUS: (businessId, startPolling = false) => ({
      businessId,
      startPolling
    }),
    API: {
      GET_BUSINESS_SYNC_STATUS: apiResourceActionCreator('businessSyncStatus')
    }
  }
});

const apiAction = (authToken, businessId) =>
  commonApiActions.authenticated.get(
    `${API_ENDPOINT}/business-tasks/${businessId}/sync-status`,
    authToken,
    actions.task.api.getBusinessSyncStatus
  );

/**
 * Periodically polls the business synchronization status every 5 seconds
 * and triggers a task refresh once a terminal state (COMPLETED, IDLE, FAILED) is reached.
 */
const pollBusinessSyncWorker = createPollingWorker({
  apiAction,
  apiTarget: actions.task.api.getBusinessSyncStatus,
  loggerName: 'Business Sync Poller',
  shouldStopPolling: (payload) => {
    const status = payload.businessSyncStatus
      ? payload.businessSyncStatus.status
      : null;
    return status === 'COMPLETED' || status === 'IDLE' || status === 'FAILED';
  },
  onComplete: function* (businessId) {
    yield put(getBusinessTasksConfig.action.task.getBusinessTasks(businessId));
  }
});

function* getBusinessSyncStatusSaga(action) {
  const { businessId, startPolling } = action.payload;

  try {
    const authToken = yield select(authTokenSelector);
    yield put(apiAction(authToken, businessId));
    const result = yield apiActionResult(
      actions.task.api.getBusinessSyncStatus
    );

    if (result.success && startPolling) {
      const currentStatus =
        result.success.payload && result.success.payload.businessSyncStatus
          ? result.success.payload.businessSyncStatus.status
          : null;

      if (currentStatus === 'PENDING' || currentStatus === 'PROCESSING') {
        yield race({
          poll: call(pollBusinessSyncWorker, businessId),
          cancel: take(LOCATION_CHANGE)
        });
      }
    }
  } catch (e) {
    console.error('[Business Sync Saga] Context initialization failed.', e);
  }
}

/**
 * Redux configuration mapping action creators, polling sagas, and slice
 * reducers for fetching and tracking business synchronization status.
 */
const configuration = {
  name: 'getBusinessSyncStatus',
  action: actions,
  sagas: getBusinessSyncStatusSaga,
  reducers: {
    request: (state) => ({ ...state }),
    success: (state, action) => ({
      ...state,
      businessSyncStatus: action.payload.businessSyncStatus
    }),
    failed: (state) => ({
      ...state,
      businessSyncStatus: 'FAILED'
    })
  }
};

export default configuration;



// WEBPACK FOOTER //
// ./src/state/modules/task/configurations/getBusinessSyncStatus.js